/* jp-drawer.js — progressive behavior for the mobile navigation drawer.
   Native details/links remain complete without JS; enhancement keeps one main
   navigation branch open at a time and publishes disclosure state for older AT. */
(() => {
  if (window.__jpDrawerInit) return;
  window.__jpDrawerInit = true;

  const pubSubEvents = () => (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);

  const init = (drawer) => {
    if (!drawer || drawer.__jpDrawerBound) return;
    drawer.__jpDrawerBound = true;
    const accordions = Array.from(drawer.querySelectorAll('.jp-drawer__acc'));
    const subgroups = Array.from(drawer.querySelectorAll('.jp-drawer__subgroup'));

    const sync = (details) => {
      const summary = details.querySelector(':scope > summary');
      if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    };

    /* Start from the calm top-level overview when the component initializes. This
       avoids dropping a shopper into a long category list merely because the
       current page belongs to that branch. Current-page links remain marked
       inside the branch when the shopper deliberately opens it, and that choice
       is retained if they close and reopen the drawer on the same page. */
    const closePrimaryAccordions = () => accordions.forEach((details) => {
      details.open = false;
      sync(details);
    });
    closePrimaryAccordions();

    accordions.forEach((details) => {
      sync(details);
      details.addEventListener('toggle', () => {
        sync(details);
        if (!details.open) return;
        accordions.forEach((other) => {
          if (other !== details && other.open) { other.open = false; sync(other); }
        });
      });
    });

    /* Nested Shop groups use the same calm one-open-at-a-time pattern, but
       only within their own top-level branch. Preserve the server-open group
       containing the current page and keep native <details> as the no-JS
       fallback. */
    const subgroupParent = (details) => details.closest('.jp-drawer__acc');
    const subgroupSets = [...new Set(subgroups.map(subgroupParent).filter(Boolean))];
    subgroupSets.forEach((parent) => {
      const siblings = subgroups.filter((details) => subgroupParent(details) === parent);
      const open = siblings.filter((details) => details.open);
      const preferred = open.find((details) => details.querySelector('[aria-current="page"]')) || open[0];
      open.forEach((details) => { if (details !== preferred) details.open = false; });
      siblings.forEach((details) => {
        sync(details);
        details.addEventListener('toggle', () => {
          sync(details);
          if (!details.open) return;
          siblings.forEach((other) => {
            if (other !== details && other.open) { other.open = false; sync(other); }
          });
        });
      });
    });
  };

  let cartTimer = 0;
  const syncCart = (cart) => {
    document.querySelectorAll('[data-jp-drawer-cart]').forEach((snap) => {
      const count = Number(cart && cart.item_count) || 0;
      snap.hidden = count === 0;
      const countEl = snap.querySelector('[data-jp-drawer-cart-count]');
      const totalEl = snap.querySelector('[data-jp-drawer-cart-total]');
      if (countEl) countEl.textContent = String(count);
      if (totalEl) {
        const currency = snap.dataset.currency || cart.currency || 'GBP';
        try {
          totalEl.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency', currency, minimumFractionDigits: cart.total_price % 100 ? 2 : 0
          }).format(cart.total_price / 100);
        } catch { totalEl.textContent = (cart.total_price / 100).toFixed(2); }
      }
    });
  };
  const refreshCart = () => {
    window.clearTimeout(cartTimer);
    cartTimer = window.setTimeout(() => {
      const root = window.Shopify?.routes?.root || '/';
      fetch(root + 'cart.js').then((response) => {
        if (!response.ok) throw new Error('http');
        return response.json();
      }).then(syncCart).catch(() => {});
    }, 120);
  };

  const boot = () => document.querySelectorAll('[data-jp-layer="drawer"]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('jp:layer:open', (event) => {
    if (!event.detail || event.detail.id !== 'drawer') return;
    const drawer = document.querySelector('[data-jp-layer="drawer"]');
    init(drawer);
    const body = drawer && drawer.querySelector('.jp-drawer__body');
    if (drawer) drawer.scrollTop = 0;
    if (body) body.scrollTop = 0;
  });
  document.addEventListener('DOMContentLoaded', () => {
    const events = pubSubEvents();
    if (typeof subscribe === 'function' && events?.cartUpdate && !window.__jpDrawerCartSubscribed) {
      window.__jpDrawerCartSubscribed = true;
      subscribe(events.cartUpdate, refreshCart);
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && !bubble.__jpDrawerObserved) {
      bubble.__jpDrawerObserved = true;
      new MutationObserver(refreshCart).observe(bubble, { childList: true, subtree: true });
    }
  });

  /* A Customizer resize or tablet rotation must not leave a hidden drawer
     owning focus, inertness and the page scroll lock after desktop nav appears. */
  const mobileHeader = window.matchMedia('(max-width: 1120px)');
  const closeAtDesktop = (event) => {
    if (event.matches) return;
    const drawer = document.querySelector('[data-jp-layer="drawer"]');
    if (!drawer?.classList.contains('is-open')) return;
    const desktopBrand = document.querySelector('.jp-header__inner .jp-wordmark');
    const layers = window.JapanScissors?.layers;
    const wasTop = layers?.top() === 'drawer';
    /* Search may have been opened from inside the drawer. Once the desktop
       breakpoint hides that drawer, its search link is no longer a valid
       focus-return target; hand the active search layer to desktop chrome. */
    if (layers?.top() === 'search') {
      const desktopSearch = document.querySelector('.jp-header__inner [data-jp-open="search"]');
      layers.setOpener?.('search', desktopSearch || desktopBrand);
    }
    layers?.close('drawer');
    if (wasTop && desktopBrand) {
      try { desktopBrand.focus({ preventScroll: true }); } catch { desktopBrand.focus(); }
    }
  };
  if (mobileHeader.addEventListener) mobileHeader.addEventListener('change', closeAtDesktop);
  else mobileHeader.addListener(closeAtDesktop);
})();
